import { useEffect, useState } from 'preact/hooks';
import { routePath } from '../routes/route';
import { useRecordsVersion } from '../lib/recordEditor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { FieldStatus } from '../components/FieldStatus';
import {
  clockTime,
  currentJob,
  dayLabel,
  jobTitle,
  jobsFrom,
  jobsToday,
  readFieldDay,
  type FieldJob,
} from '../lib/fieldDay';
import './TechToday.css';

// S5a — TODAY, FOR THE TECH (wf03 §01, blessed 2026-08-06): "In a truck, in
// a basement, with gloves on."
//
// The day in order, earliest first: time, who and where, the one-line issue,
// and the urgent flag where the office set one. The visit the tech is on now
// is marked as such ("09:00 · now") because on a phone the answer to "which
// one am I on" has to be visible without reading the clock.
//
// WHOSE DAY. The tech's own, and only because the SERVER says so: the Rexx
// pack puts `hvac_visit` in `readScope` with `scopedRoles: ['tech']`, so row
// scope has already filtered before this screen sees a row. There is no
// client-side filter here and no tech id is passed — see lib/fieldDay's own
// header. A tech who can see somebody else's visit is a Core bug to report,
// not a thing to paper over on this screen.
//
// WHAT IT COSTS. Four requests' worth of types, none of them per row (see
// lib/fieldDay). The budget is asserted in test/requestBudget.test.tsx.
//
// EMPTY IS NOT BROKEN. A tech with no visits today is the ordinary case at
// 7am and again at 6pm, so the empty state is a real sentence rather than
// the absence of rows — and a read that FAILED says so instead, which is the
// distinction S15/S16 spent a chunk establishing and the one that matters
// most on the surface most likely to be offline.

export interface TechTodayProps {
  tenantId: string;
  userName: string;
  onNavigate: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

export function TechToday({ tenantId, userName, onNavigate }: TechTodayProps) {
  const [load, setLoad] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [jobs, setJobs] = useState<FieldJob[]>([]);
  const [unanswered, setUnanswered] = useState(0);
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();
  const now = new Date();

  useEffect(() => {
    let cancelled = false;
    async function load_() {
      setLoad('loading');
      setFailure(null);
      try {
        const read = await readFieldDay(tenantId);
        if (cancelled) return;
        setJobs(jobsToday(jobsFrom(read), new Date()));
        setUnanswered(read.unanswered);
        setLoad('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLoad('error');
      }
    }
    void load_();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion, retryNonce]);

  const current = load === 'ready' ? currentJob(jobs, now) : null;

  return (
    <div class="tech-today">
      <FieldStatus />

      <header class="tech-today__head">
        <h1 class="tech-today__title">Today</h1>
        <div class="tech-today__sub">
          {dayLabel(now)} · {userName}
        </div>
      </header>

      <div class="tech-today__body">
        {load === 'loading' ? <div class="tech-today__say">One moment…</div> : null}

        {load === 'error' && failure !== null ? (
          <div class="tech-today__say">
            {failure.headline}
            <span class="tech-today__say-soft">{failure.detail}</span>
            {failure.retryable ? (
              <button
                type="button"
                class="tech-today__retry"
                onClick={() => setRetryNonce((n) => n + 1)}
              >
                Read it again
              </button>
            ) : null}
          </div>
        ) : null}

        {load === 'ready' && jobs.length === 0 ? (
          <div class="tech-today__empty">
            <b>Nothing booked for you today.</b>
            When dispatch puts a job on your name it lands here, in the order you'll drive them.
          </div>
        ) : null}

        {load === 'ready' && jobs.length > 0
          ? jobs.map((job) => (
              <JobRow
                key={job.visit.id}
                job={job}
                now={current !== null && current.visit.id === job.visit.id}
                onOpen={() =>
                  onNavigate(
                    job.workOrder
                      ? routePath({ screen: 'jobDetail', id: job.workOrder.id })
                      : routePath({ screen: 'jobs' })
                  )
                }
              />
            ))
          : null}

        {load === 'ready' && unanswered > 0 ? (
          // S16's rule on the phone: rows the server declined to answer for
          // are COUNTED, never silently missing. Not styled as an error —
          // it is a statement about the read, not about the day.
          <div class="tech-today__soft">
            {unanswered === 1 ? 'One record' : `${unanswered} records`} could not be read, so this
            day may be missing something.
          </div>
        ) : null}
      </div>
    </div>
  );
}

function JobRow({ job, now, onOpen }: { job: FieldJob; now: boolean; onOpen: () => void }) {
  const time = clockTime(job.startsAt);
  return (
    <button type="button" class="tech-today__job" onClick={onOpen}>
      <span
        class="tech-today__stripe"
        style={{ background: job.urgent ? 'var(--status-critical)' : 'transparent' }}
      />
      <span class="tech-today__job-main">
        <span class="tech-today__tm">
          {time || 'No time set'}
          {now ? ' · now' : ''}
        </span>
        <b>{jobTitle(job)}</b>
        <small>
          {job.issue || (job.workOrder === null ? "Couldn't read this job" : 'No issue recorded')}
          {job.urgent ? ' · urgent' : ''}
        </small>
      </span>
    </button>
  );
}
